import React, { useState } from "react";
import {
  LayoutGrid, Search, Users, FolderKanban, ShieldCheck, Bell, Star, Settings,
  Inbox, LayoutTemplate, Handshake, CheckCircle2, Circle, FileText, ChevronDown,
  Undo2, Redo2, Bold, Italic, Underline, List, ListOrdered, AlignLeft, AlignRight,
  Link2, Image as ImageIcon, Table2, Sparkles, MoreHorizontal, Send, Plus,
  Building2, Cpu, Boxes, Target, Share2, MapPin, Award, Gauge, TrendingUp,
  Landmark, Bookmark, MessageSquare,
} from "lucide-react";
import { WindowChrome, ScoreRing, MockScroll } from "./shared";
import { useLang } from "../../i18n";

/* ----------------------------- primitives ----------------------------- */
const Frame = ({ label, children, className = "" }) => (
  <div className={`overflow-hidden rounded-2xl border border-[var(--border)] bg-white shadow-lg-soft ${className}`}>
    <WindowChrome label={label} />
    {children}
  </div>
);

const Tag = ({ children, tone }) => {
  const tones = {
    blue: "bg-blue-50 text-blue-700", green: "bg-emerald-50 text-emerald-700",
    pink: "bg-pink-50 text-pink-600", amber: "bg-amber-50 text-amber-700",
    slate: "bg-slate-100 text-slate-600",
  };
  return <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-[10px] font-medium ${tones[tone]}`}>{children}</span>;
};

const Mono = ({ text, color }) => (
  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-[11px] font-bold text-white" style={{ background: color }}>{text}</span>
);

const NavItem = ({ icon: Icon, label, active }) => (
  <div className={`flex items-center gap-2.5 rounded-lg px-3 py-2 text-[12px] ${active ? "bg-white/12 font-medium text-white" : "text-white/55"}`}>
    <Icon className="h-4 w-4" strokeWidth={1.6} /><span className="truncate">{label}</span>
  </div>
);

const NavySidebar = ({ items, className = "" }) => (
  <div className={`flex flex-col bg-[var(--navy)] p-3 ${className}`}>
    <div className="flex items-center gap-2 px-1.5 pb-4">
      <span className="font-heading text-[15px] font-bold text-white">Procur<span className="text-blue-400">mo</span></span>
    </div>
    <div className="space-y-0.5">{items.map((it) => <NavItem key={it.label} {...it} />)}</div>
  </div>
);

/* ============================ HERO MOCK ============================ */
export const HeroMock = () => {
  const { lang } = useLang();
  const de = lang === "de";
  const rows = [
    { t: de ? "Digitalisierung der Verwaltung" : "Admin digitalization", a: "Deutsche Bahn AG", s: 94, tone: "blue", cat: "IT" },
    { t: de ? "Neubau Grundschule" : "New school build", a: "Stadt Musterstadt", s: 88, tone: "green", cat: de ? "Bau" : "Build" },
    { t: de ? "IT-Betrieb & Support" : "IT operations", a: de ? "Bundesministerium" : "Ministry", s: 79, tone: "blue", cat: "IT" },
  ];
  return (
    <Frame label="app.procurmo.io">
      <div className="flex">
        <NavySidebar className="hidden w-[168px] shrink-0 sm:flex" items={[
          { icon: LayoutGrid, label: de ? "Übersicht" : "Overview", active: true },
          { icon: Search, label: de ? "Ausschreibungen" : "Tenders" },
          { icon: Users, label: "Partner" },
          { icon: FolderKanban, label: de ? "Projekte" : "Projects" },
          { icon: ShieldCheck, label: "Compliance" },
        ]} />
        <div className="min-w-0 flex-1 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 rounded-lg border border-[var(--border)] bg-[var(--surface-2)] px-2.5 py-1.5">
              <Search className="h-3.5 w-3.5 text-slate-400" strokeWidth={1.6} />
              <span className="text-[11px] text-slate-400">{de ? "Suchen …" : "Search …"}</span>
            </div>
            <span className="relative grid h-8 w-8 place-items-center rounded-lg border border-[var(--border)]">
              <Bell className="h-4 w-4 text-slate-500" strokeWidth={1.6} />
              <span className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-[var(--blue)]" />
            </span>
          </div>
          <div className="mt-4 grid grid-cols-3 gap-2.5">
            {[[de ? "Aktiv" : "Active", "24"], [de ? "Favoriten" : "Saved", "8"], [de ? "Ø Score" : "Avg score", "86"]].map(([k, v]) => (
              <div key={k} className="rounded-xl border border-[var(--border)] bg-white p-2.5">
                <div className="text-[10px] uppercase tracking-wide text-slate-400">{k}</div>
                <div className="font-heading text-xl font-bold text-[var(--ink)]">{v}</div>
              </div>
            ))}
          </div>
          <div className="mt-3 rounded-xl border border-[var(--border)]">
            <div className="px-3 py-2 text-[11px] font-medium text-slate-400">{de ? "Empfohlene Ausschreibungen" : "Recommended tenders"}</div>
            {rows.map((r) => (
              <div key={r.t} className="flex items-center gap-2 border-t border-[var(--border)] px-3 py-2.5">
                <Star className="h-3.5 w-3.5 shrink-0 fill-amber-400 text-amber-400" />
                <div className="min-w-0 flex-1"><div className="truncate text-[12px] font-medium text-[var(--ink)]">{r.t}</div><div className="truncate text-[10px] text-slate-400">{r.a}</div></div>
                <Tag tone={r.tone}>{r.cat}</Tag>
                <span className="font-mono text-[11px] font-semibold text-[var(--blue)]">{r.s}</span>
              </div>
            ))}
          </div>
          <div className="mt-3 flex items-center gap-3 rounded-xl border border-[var(--border)] bg-[var(--blue-soft)]/60 p-3">
            <ScoreRing value={92} size={46} />
            <div><div className="text-[10px] text-slate-500">{de ? "Bester Partner-Match" : "Top partner match"}</div><div className="text-[12px] font-semibold text-[var(--ink)]">TechSolutions GmbH</div></div>
            <span className="ml-auto inline-flex items-center gap-1 rounded-md bg-[var(--green-soft)] px-2 py-1 text-[10px] font-medium text-[var(--green)]"><TrendingUp className="h-3 w-3" />+ Fit</span>
          </div>
        </div>
      </div>
    </Frame>
  );
};

/* ==================== 1) TENDER SHOWCASE ==================== */
const PORTALS = [
  { id: "dvp", name: "Deutsches Vergabeportal", mono: "DTVP", color: "#0b3a67", sub: { de: "Öffentliche Ausschreibungen", en: "Public tenders" } },
  { id: "subreport", name: "subreport", color: "#004f9f", sub: { de: "Ausschreibungen & Aufträge", en: "Tenders & contracts" } },
  { id: "nrw", name: "vergabe.NRW", color: "#009036", sub: { de: "Landesvergabestelle NRW", en: "State of NRW" } },
  { id: "berlin", name: "Berlin Vergabemarkt", color: "#e3000f", sub: { de: "Ausschreibungen Berlin", en: "Berlin tenders" } },
  { id: "bayern", name: "Bayern Portal", color: "#0066b3", sub: { de: "Ausschreibungen Bayern", en: "Bavaria tenders" } },
  { id: "bund", name: "bund.de", color: "#1f2937", sub: { de: "Ausschreibungen des Bundes", en: "Federal tenders" } },
];

/* Brand-styled wordmark (nominative reference to public data sources — not embedded logo files). */
const PortalWordmark = ({ p, size = "sm" }) => {
  const s = size === "sm" ? "text-[12px]" : "text-[13px]";
  const c = p.color;
  switch (p.id) {
    case "dvp":
      return <span className={`font-heading font-extrabold tracking-tight ${s}`} style={{ color: c }}>DTVP</span>;
    case "subreport":
      return <span className={`font-heading font-bold lowercase tracking-tight ${s}`} style={{ color: c }}>subreport</span>;
    case "nrw":
      return <span className={`font-heading font-bold tracking-tight ${s}`} style={{ color: "#333" }}>vergabe<span style={{ color: c }}>.NRW</span></span>;
    case "berlin":
      return <span className={`font-heading font-bold tracking-tight ${s}`}><span style={{ color: c }}>Berlin</span> <span style={{ color: "#1f2937" }}>Vergabemarkt</span></span>;
    case "bayern":
      return <span className={`font-heading font-bold tracking-tight ${s}`} style={{ color: c }}>Bayern<span className="text-slate-400 font-medium"> Portal</span></span>;
    case "bund":
      return <span className={`font-heading font-bold tracking-tight ${s}`} style={{ color: c }}>bund<span style={{ color: "#e3000f" }}>.</span>de</span>;
    default:
      return <span className={`font-heading font-bold ${s}`} style={{ color: c }}>{p.name}</span>;
  }
};

/* Small favicon-style brand tile for the portal (brand color, no protected artwork). */
const PortalTile = ({ p }) => {
  const initials = { dvp: "DV", subreport: "sr", nrw: "NRW", berlin: "B", bayern: "BY", bund: "bd" }[p.id] || "?";
  return (
    <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-[10px] font-extrabold text-white" style={{ background: p.color }}>{initials}</span>
  );
};

export const TenderShowcase = () => {
  const { lang } = useLang();
  const de = lang === "de";
  const rows = [
    { org: "Deutsche Bahn AG", t: de ? "Digitalisierung der Verwaltung" : "Public admin digitalization", cat: "IT & Software", tone: "blue", region: "Berlin", pub: "12.05", due: "15.06", mono: "DB", color: "#dc2626" },
    { org: "Stadt Musterstadt", t: de ? "Neubau Grundschule Musterstadt" : "New primary school", cat: de ? "Bauleistungen" : "Construction", tone: "green", region: "NRW", pub: "11.05", due: "02.06", mono: "SM", color: "#059669" },
    { org: de ? "Bundesministerium des Innern" : "Federal Ministry", t: de ? "IT-Betrieb und Support" : "IT operations & support", cat: "IT & Software", tone: "blue", region: "Bonn", pub: "10.05", due: "01.06", mono: "BMI", color: "#1d4ed8" },
    { org: de ? "Landeshauptstadt München" : "City of Munich", t: de ? "Rahmenvertrag Reinigungsdienst" : "Cleaning framework", cat: de ? "Dienstleistungen" : "Services", tone: "pink", region: "Bayern", pub: "09.05", due: "26.05", mono: "M", color: "#0f172a" },
    { org: de ? "Universität Hamburg" : "Univ. of Hamburg", t: de ? "Lieferung von Büromöbeln" : "Office furniture supply", cat: de ? "Lieferungen" : "Supply", tone: "amber", region: "Hamburg", pub: "08.05", due: "25.05", mono: "UH", color: "#b45309" },
  ];
  return (
    <div className="grid grid-cols-12 items-center gap-6 lg:gap-4">
      {/* Portals */}
      <div className="col-span-12 lg:col-span-4">
        <div className="space-y-2">
          {PORTALS.map((p) => (
            <div key={p.name} className="flex items-center gap-3 rounded-xl border border-[var(--border)] bg-white px-3 py-2.5 shadow-md-soft">
              <PortalTile p={p} />
              <div className="min-w-0"><PortalWordmark p={p} size="lg" /><div className="truncate text-[10px] text-slate-400">{p.sub[lang] || p.sub.de}</div></div>
              <span className="ml-auto h-2 w-2 rounded-full bg-[var(--green)]" />
            </div>
          ))}
          <div className="flex items-center gap-3 rounded-xl border border-dashed border-[var(--border)] px-3 py-2.5 text-[11px] text-slate-400">
            <span className="grid h-8 w-8 place-items-center rounded-lg border border-dashed border-slate-300"><Plus className="h-4 w-4" /></span>
            {de ? "Weitere Quellen · EU, TED, KOINNO" : "More sources · EU, TED, KOINNO"}
          </div>
        </div>
      </div>
      {/* Hub connector — aggregation node with converging flow lines */}
      <div className="relative col-span-12 hidden items-center justify-center py-2 lg:col-span-2 lg:flex">
        <svg viewBox="0 0 170 300" preserveAspectRatio="none" className="pointer-events-none absolute inset-0 h-full w-full">
          {[26, 92, 150, 208, 274].map((sy) => (
            <path key={`flow-${sy}`} d={`M0,${sy} C64,${sy} 72,150 88,150`} fill="none" stroke="#2563eb" strokeWidth="1.5" strokeLinecap="round" vectorEffect="non-scaling-stroke" opacity="0.3" className="edge-flow" />
          ))}
          <path d="M88,150 C128,150 140,150 170,150" fill="none" stroke="#2563eb" strokeWidth="2.2" strokeLinecap="round" vectorEffect="non-scaling-stroke" opacity="0.5" className="edge-flow" />
        </svg>
        <div className="relative z-10 flex flex-col items-center gap-2">
          <div className="relative grid h-16 w-16 place-items-center rounded-2xl text-white shadow-lg-soft" style={{ background: "linear-gradient(140deg,#3b76ff,#1746c9)" }}>
            <span className="absolute inset-0 -z-10 animate-ping rounded-2xl bg-[var(--blue)] opacity-25" />
            <span className="absolute -inset-2 -z-20 rounded-3xl bg-[var(--blue)] opacity-10 blur-lg" />
            <Boxes className="h-7 w-7" strokeWidth={1.7} />
          </div>
          <span className="whitespace-nowrap rounded-full bg-[var(--blue-soft)] px-2.5 py-0.5 text-[10px] font-semibold text-[var(--blue)]">{de ? "KI-Aggregation" : "AI aggregation"}</span>
        </div>
      </div>
      {/* Feed dashboard */}
      <div className="col-span-12 lg:col-span-6">
        <MockScroll>
        <Frame label="procurmo · ausschreibungen" className="min-w-[540px] lg:min-w-0">
          <div className="flex">
            <NavySidebar className="flex w-[150px] shrink-0" items={[
              { icon: LayoutGrid, label: de ? "Übersicht" : "Overview" },
              { icon: Search, label: de ? "Ausschreibungen" : "Tenders", active: true },
              { icon: Users, label: "Partner" },
              { icon: FolderKanban, label: de ? "Projekte" : "Projects" },
              { icon: Inbox, label: de ? "Nachrichten" : "Messages" },
              { icon: Star, label: de ? "Merkliste" : "Saved" },
            ]} />
            <div className="min-w-0 flex-1 p-3.5">
              <div className="flex items-center justify-between">
                <div><div className="text-[13px] font-bold text-[var(--ink)]">{de ? "Alle Ausschreibungen" : "All tenders"}</div><div className="font-mono text-[10px] text-slate-400">1.248 {de ? "Ergebnisse" : "results"}</div></div>
                <div className="flex items-center gap-2 rounded-lg border border-[var(--border)] bg-[var(--surface-2)] px-2.5 py-1.5"><Search className="h-3.5 w-3.5 text-slate-400" /><span className="text-[11px] text-slate-400">{de ? "Suche…" : "Search…"}</span></div>
              </div>
              <div className="mt-3 space-y-1.5">
                {rows.map((r) => (
                  <div key={r.t} className="flex items-center gap-2.5 rounded-xl border border-[var(--border)] bg-white px-3 py-2.5 transition-colors hover:border-[var(--blue)]/40">
                    <Mono text={r.mono} color={r.color} />
                    <div className="min-w-0 flex-1"><div className="truncate text-[12px] font-medium text-[var(--ink)]">{r.t}</div><div className="truncate text-[10px] text-slate-400">{r.org} · {de ? "Frist" : "Due"} {r.due}</div></div>
                    <div className="hidden items-center gap-1 text-[10px] text-slate-400 sm:flex"><MapPin className="h-3 w-3" />{r.region}</div>
                    <Tag tone={r.tone}>{r.cat}</Tag>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Frame>
        </MockScroll>
      </div>
    </div>
  );
};

/* ==================== 2) PARTNER SHOWCASE ==================== */
const PipelineTop = () => {
  const { t } = useLang();
  const icons = [Boxes, Cpu, Share2, Target];
  return (
    <div className="mb-8 flex flex-wrap items-center justify-center gap-x-4 gap-y-3">
      {t.partner.pipeline.map((st, i) => {
        const Icon = icons[i];
        return (
          <React.Fragment key={st.t}>
            <div className="flex items-center gap-2.5">
              <span className="grid h-10 w-10 place-items-center rounded-xl text-white shadow-md-soft" style={{ background: "linear-gradient(140deg,#3b76ff,#1746c9)" }}><Icon className="h-5 w-5" strokeWidth={1.6} /></span>
              <div><div className="text-[12px] font-bold text-[var(--ink)]">{st.t}</div><div className="text-[10px] text-slate-400">{st.s}</div></div>
            </div>
            {i < 3 && <span className="hidden text-slate-300 sm:block">→</span>}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const NetNode = ({ x, y, name, score, color, primary }) => (
  <div className="absolute flex -translate-x-1/2 -translate-y-1/2 flex-col items-center" style={{ left: `${(x / 520) * 100}%`, top: `${(y / 430) * 100}%` }}>
    {primary ? (
      <div className="relative grid h-[64px] w-[64px] place-items-center rounded-2xl text-white shadow-lg-soft" style={{ background: "linear-gradient(140deg,#3b76ff,#1746c9)" }}>
        <span className="absolute inset-0 -z-10 animate-ping rounded-2xl bg-[var(--blue)] opacity-25" />
        <Building2 className="h-7 w-7" strokeWidth={1.7} />
      </div>
    ) : (
      <div className="grid h-[52px] w-[52px] place-items-center rounded-2xl bg-white" style={{ boxShadow: `0 0 0 2px ${color}22, var(--shadow-md)` }}>
        <Building2 className="h-6 w-6" style={{ color }} strokeWidth={1.7} />
      </div>
    )}
    <div className={`mt-1.5 max-w-[92px] text-center text-[10px] font-semibold leading-tight ${primary ? "text-[var(--ink)]" : "text-slate-600"}`}>{name}</div>
    {score && (
      <span className="mt-0.5 rounded-full px-1.5 py-0.5 text-[9px] font-bold" style={{ color, background: `${color}18` }}>{score}%</span>
    )}
  </div>
);

export const PartnerShowcase = () => {
  const { t, lang } = useLang();
  const de = lang === "de";
  const nodes = [
    { x: 260, y: 205, name: de ? "Ihr Unternehmen" : "Your company", color: "#2563eb", primary: true },
    { x: 240, y: 58, name: "TechSolutions GmbH", score: 92, color: "#2563eb" },
    { x: 430, y: 100, name: "InfraBuild AG", score: 87, color: "#7c3aed" },
    { x: 82, y: 160, name: "Planwerk GmbH", score: 89, color: "#2563eb" },
    { x: 452, y: 255, name: "Construct Partner", score: 82, color: "#d97706" },
    { x: 118, y: 332, name: "EcoSystems GmbH", score: 85, color: "#16a34a" },
    { x: 282, y: 372, name: "DataCore GmbH", score: 78, color: "#7c3aed" },
  ];
  const edgeColor = (s) => {
    if (s >= 86) return "#16a34a";
    if (s >= 70) return "#2563eb";
    return "#7c3aed";
  };
  const edgePath = (x, y) => {
    const cx = 260, cy = 205;
    const mx = (cx + x) / 2, my = (cy + y) / 2;
    const dx = x - cx, dy = y - cy;
    return `M${cx},${cy} Q${mx - dy * 0.12},${my + dx * 0.12} ${x},${y}`;
  };
  const attrs = [
    [de ? "Leistungserfahrung" : "Experience", 95], [de ? "Technische Expertise" : "Technical expertise", 93],
    [de ? "Kapazität & Verfügbarkeit" : "Capacity", 90], [de ? "Zertifizierungen" : "Certifications", 88],
    [de ? "Regionale Nähe" : "Regional proximity", 85], [de ? "Finanzielle Stabilität" : "Financial stability", 80],
  ];
  return (
    <div>
      <PipelineTop />
      <div className="grid grid-cols-12 gap-5">
        {/* Data sources */}
        <div className="col-span-12 lg:col-span-3">
          <div className="rounded-2xl border border-[var(--border)] bg-white p-4 shadow-md-soft">
            <div className="mb-3 text-[11px] font-bold uppercase tracking-wide text-[var(--ink)]">Data Sources</div>
            <div className="space-y-2.5">
              {PORTALS.map((p) => (
                <div key={p.name} className="flex items-center gap-2.5">
                  <PortalTile p={p} />
                  <div className="min-w-0 flex-1"><PortalWordmark p={p} /></div>
                  <span className="h-1.5 w-1.5 rounded-full bg-[var(--green)]" />
                </div>
              ))}
              <div className="pt-1 text-[11px] font-medium text-[var(--blue)]">+ {de ? "Weitere Quellen" : "More sources"}</div>
            </div>
          </div>
        </div>
        {/* Network */}
        <div className="col-span-12 lg:col-span-5">
          <MockScroll>
          <div className="relative w-full min-w-[480px] overflow-hidden rounded-2xl border border-[var(--border)] bg-white lg:min-w-0" style={{ paddingBottom: "82.7%" }}>
            <div className="absolute inset-0 net-grid" />
            <div className="absolute left-1/2 top-[48%] h-40 w-40 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[var(--blue)] opacity-[0.06] blur-2xl" />
            <svg viewBox="0 0 520 430" preserveAspectRatio="none" className="absolute inset-0 h-full w-full">
              {nodes.slice(1).map((n) => (
                <g key={n.name}>
                  <path d={edgePath(n.x, n.y)} fill="none" stroke={edgeColor(n.score)} strokeWidth="5" strokeLinecap="round" vectorEffect="non-scaling-stroke" opacity="0.12" />
                  <path d={edgePath(n.x, n.y)} className="edge-flow" fill="none" stroke={edgeColor(n.score)} strokeWidth="1.8" strokeLinecap="round" vectorEffect="non-scaling-stroke" opacity="0.7" />
                </g>
              ))}
            </svg>
            {nodes.map((n) => <NetNode key={n.name} {...n} />)}
          </div>
          </MockScroll>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-x-5 gap-y-1.5 text-[11px] text-slate-500">
            <span className="flex items-center gap-1.5"><span className="h-1 w-5 rounded-full bg-[var(--green)]" />{de ? "Hoch (>85%)" : "High (>85%)"}</span>
            <span className="flex items-center gap-1.5"><span className="h-1 w-5 rounded-full bg-[var(--blue)]" />{de ? "Mittel (70–85%)" : "Medium (70–85%)"}</span>
            <span className="flex items-center gap-1.5"><span className="h-1 w-5 rounded-full bg-[var(--purple)]" />{de ? "Niedrig (<70%)" : "Low (<70%)"}</span>
          </div>
        </div>
        {/* Match panel */}
        <div className="col-span-12 lg:col-span-4">
          <div className="overflow-hidden rounded-2xl text-white shadow-lg-soft" style={{ background: "linear-gradient(160deg,#12244d,#0a1730)" }}>
            <div className="border-b border-white/10 p-4">
              <div className="flex items-center justify-between">
                <span className="rounded-md bg-[var(--green)]/20 px-2 py-0.5 text-[10px] font-semibold text-emerald-300">Top Match</span>
                <span className="font-heading text-3xl font-extrabold text-emerald-400">92%</span>
              </div>
              <div className="mt-2 flex items-center gap-2.5">
                <span className="grid h-9 w-9 place-items-center rounded-lg bg-white/10"><Building2 className="h-5 w-5 text-blue-300" strokeWidth={1.6} /></span>
                <div><div className="text-[13px] font-semibold">TechSolutions GmbH</div><div className="text-[10px] text-white/50">{de ? "München · Kompatibilitäts-Score" : "Munich · compatibility score"}</div></div>
              </div>
            </div>
            <div className="p-4">
              <div className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-white/50">Attribute Match</div>
              <div className="space-y-2">
                {attrs.map(([l, v]) => (
                  <div key={l} className="flex items-center gap-2">
                    <span className="w-[104px] shrink-0 truncate text-[10px] text-white/70">{l}</span>
                    <div className="h-1.5 flex-1 rounded-full bg-white/10"><div className="h-1.5 rounded-full bg-emerald-400" style={{ width: `${v}%` }} /></div>
                    <span className="w-7 text-right text-[10px] font-semibold">{v}%</span>
                  </div>
                ))}
              </div>
              <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 rounded-lg bg-white/5 p-2.5 text-[10px]">
                <div className="flex justify-between"><span className="text-white/45">{de ? "Mitarbeiter" : "Employees"}</span><span className="font-medium">120</span></div>
                <div className="flex justify-between"><span className="text-white/45">{de ? "Gründung" : "Founded"}</span><span className="font-medium">2010</span></div>
                <div className="flex justify-between"><span className="text-white/45">{de ? "Referenzen" : "References"}</span><span className="font-medium">42</span></div>
                <div className="flex justify-between"><span className="text-white/45">{de ? "Umsatz" : "Revenue"}</span><span className="font-medium">10–50 Mio.</span></div>
              </div>
              <div className="mt-3 flex items-center gap-2">
                <button className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-[var(--blue)] py-2 text-[11px] font-semibold text-white transition-colors hover:bg-[var(--blue-2)]">{de ? "Als Partner vorschlagen" : "Suggest as partner"}</button>
                <span className="grid h-8 w-8 place-items-center rounded-lg border border-white/15 text-white/60"><Bookmark className="h-4 w-4" strokeWidth={1.6} /></span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ==================== 3) WORKSPACE SHOWCASE ==================== */
export const WorkspaceShowcase = () => {
  const { lang } = useLang();
  const de = lang === "de";
  const tabs = ["Übersicht", "Planung", "Angebot", "Dokumente", "Kommunikation", "Abgabe"];
  const sections = [
    [de ? "Projektverständnis" : "Project understanding", true], [de ? "Lösungskonzept" : "Solution concept", true],
    [de ? "Technische Umsetzung" : "Technical delivery", false], ["Zeitplan", false],
    [de ? "Team & Qualifikation" : "Team & qualification", false], ["Referenzen", false], ["Preisangebot", false],
  ];
  const files = ["Projektbeschreibung.pdf", "Vorentwurf_Architektur.ifc", "Nachhaltigkeitskonzept.pdf", "Kostenkalkulation.xlsx"];
  const activity = [
    ["Lukas Schneider", de ? "hat Abschnitt bearbeitet" : "edited a section", "#16a34a", "2 Min."],
    ["Anna Becker", de ? "hat kommentiert" : "added a comment", "#ec4899", "10 Min."],
    ["Marco Rossi", de ? "schlug Änderung vor" : "proposed a change", "#2563eb", "15 Min."],
  ];
  const Tool = ({ icon: Icon }) => <span className="grid h-6 w-6 place-items-center rounded text-slate-500 hover:bg-slate-100"><Icon className="h-3.5 w-3.5" strokeWidth={1.7} /></span>;
  return (
    <MockScroll>
    <Frame label="procurmo · consortium-workspace" className="min-w-[880px] lg:min-w-0">
      <div className="grid grid-cols-12">
        {/* Sidebar */}
        <NavySidebar className="col-span-2 flex" items={[
          { icon: LayoutGrid, label: "Dashboard" }, { icon: Search, label: "Opportunities" },
          { icon: Users, label: "Consortia" }, { icon: FolderKanban, label: de ? "Projekte" : "Projects", active: true },
          { icon: Inbox, label: "Messages" }, { icon: Handshake, label: "Partners" },
          { icon: LayoutTemplate, label: "Templates" }, { icon: ShieldCheck, label: "Compliance" }, { icon: Settings, label: "Settings" },
        ]} />
        {/* Editor */}
        <div className="col-span-7">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[var(--border)] px-4 py-2.5">
            <div>
              <div className="flex items-center gap-2"><span className="text-[13px] font-bold text-[var(--ink)]">Neubau Grundschule Musterstadt</span><span className="inline-flex items-center gap-1 text-[10px] text-emerald-600"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />{de ? "In Bearbeitung" : "In progress"}</span></div>
              <div className="mt-1.5 flex gap-3 text-[11px]">{tabs.map((tb, i) => <span key={tb} className={i === 2 ? "font-semibold text-[var(--blue)]" : "text-slate-400"}>{tb}</span>)}</div>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex -space-x-1.5">{["#2563eb", "#16a34a", "#ec4899"].map((c) => <span key={c} className="h-5 w-5 rounded-full ring-2 ring-white" style={{ background: c }} />)}<span className="grid h-5 w-5 place-items-center rounded-full bg-slate-200 text-[8px] font-bold text-slate-500 ring-2 ring-white">+2</span></div>
              <button className="rounded-lg bg-[var(--blue)] px-2.5 py-1 text-[10px] font-semibold text-white">{de ? "Einladen" : "Invite"}</button>
            </div>
          </div>
          <div className="flex items-center gap-0.5 border-b border-[var(--border)] px-4 py-1.5">
            <Tool icon={Undo2} /><Tool icon={Redo2} /><span className="mx-1 h-4 w-px bg-slate-200" />
            <span className="rounded px-1.5 text-[10px] text-slate-500">Normal</span><ChevronDown className="h-3 w-3 text-slate-400" />
            <span className="mx-1 h-4 w-px bg-slate-200" /><Tool icon={Bold} /><Tool icon={Italic} /><Tool icon={Underline} />
            <span className="mx-1 h-4 w-px bg-slate-200" /><Tool icon={List} /><Tool icon={ListOrdered} /><Tool icon={Link2} /><Tool icon={ImageIcon} /><Tool icon={Table2} />
            <span className="ml-auto inline-flex items-center gap-1 rounded-md bg-[var(--blue-soft)] px-2 py-1 text-[10px] font-semibold text-[var(--blue)]"><Sparkles className="h-3 w-3" />{de ? "KI verbessern" : "AI improve"}</span>
          </div>
          <div className="p-4">
            <div className="font-heading text-lg font-bold text-[var(--ink)]">Technisches Konzept</div>
            <div className="mt-2"><span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700">Lukas Schneider</span></div>
            <p className="mt-1.5 rounded bg-emerald-50/60 px-1 text-[11px] leading-relaxed text-slate-600">{de ? "Unser Ansatz basiert auf einem nachhaltigen und zukunftssicheren Gebäudekonzept, das höchste Qualitätsstandards erfüllt." : "Our approach is based on a sustainable, future-proof building concept meeting the highest standards."}</p>
            <div className="mt-3 text-[13px] font-bold text-[var(--ink)]">1. {de ? "Projektverständnis" : "Project understanding"}</div>
            <p className="mt-1 text-[11px] leading-relaxed text-slate-600">{de ? "Das Ziel ist die Errichtung einer modernen, energieeffizienten Grundschule, die den pädagogischen Anforderungen gerecht wird." : "The goal is a modern, energy-efficient primary school meeting pedagogical requirements."}</p>
            <div className="mt-2"><span className="rounded bg-pink-100 px-1.5 py-0.5 text-[10px] font-medium text-pink-600">Anna Becker</span></div>
            <p className="mt-1.5 rounded bg-pink-50/70 px-1 text-[11px] leading-relaxed text-slate-600">{de ? "Wir verstehen die Anforderungen und vereinen Funktionalität, Nachhaltigkeit und Wirtschaftlichkeit." : "We understand the requirements and combine functionality, sustainability and cost-efficiency."} <span className="inline-flex items-center gap-0.5 align-middle text-slate-400"><MessageSquare className="h-3 w-3" />2</span></p>
          </div>
        </div>
        {/* Right column */}
        <div className="col-span-3 space-y-3 border-l border-[var(--border)] p-3.5">
          <div className="rounded-xl border border-[var(--border)] p-3">
            <div className="mb-1 text-[11px] font-semibold text-[var(--ink)]">{de ? "Fortschritt" : "Progress"}</div>
            <div className="flex items-center gap-3"><ScoreRing value={68} size={44} /><div className="text-[10px] text-slate-500">{de ? "4 von 7 Abschnitten abgeschlossen" : "4 of 7 sections done"}</div></div>
          </div>
          <div>
            <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">{de ? "Abschnitte" : "Sections"}</div>
            <div className="space-y-1">
              {sections.map(([s, done], i) => (
                <div key={s} className="flex items-center gap-1.5 text-[11px] text-slate-600"><span className="w-3 text-slate-400">{i + 1}.</span><span className="flex-1 truncate">{s}</span>{done ? <CheckCircle2 className="h-3.5 w-3.5 text-[var(--blue)]" strokeWidth={1.7} /> : <Circle className="h-3.5 w-3.5 text-slate-300" strokeWidth={1.7} />}</div>
              ))}
            </div>
          </div>
          <div>
            <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">{de ? "Aktivitäten" : "Activity"}</div>
            <div className="space-y-1.5">
              {activity.map(([who, txt, clr, t]) => (
                <div key={who} className="flex items-start gap-1.5"><span className="mt-0.5 h-4 w-4 shrink-0 rounded-full" style={{ background: clr }} /><div className="text-[10px] leading-tight text-slate-500"><span className="font-medium text-slate-700">{who}</span> {txt}<div className="text-[9px] text-slate-400">{de ? "vor" : ""} {t}</div></div></div>
              ))}
            </div>
          </div>
        </div>
      </div>
      {/* Feature strip */}
      <div className="grid grid-cols-4 gap-2 border-t border-[var(--border)] bg-[var(--surface-2)]/60 px-4 py-3">
        {[[Users, de ? "Echtzeit-Zusammenarbeit" : "Real-time collaboration"], [CheckCircle2, de ? "Klare Aufgaben" : "Clear tasks"], [FileText, de ? "Versionskontrolle" : "Version control"], [ShieldCheck, de ? "DSGVO-konform" : "GDPR-ready"]].map(([Icon, l]) => (
          <div key={l} className="flex items-center gap-1.5 text-[10px] text-slate-500"><Icon className="h-3.5 w-3.5 text-[var(--blue)]" strokeWidth={1.6} /><span className="truncate">{l}</span></div>
        ))}
      </div>
    </Frame>
    </MockScroll>
  );
};
